import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, StatusPill, fmtMoney, fmtDate, fmtDateTime, yearOptions, currentYear, currentWeekOf, useIsMobile, mobile } from '../components/UI';
import Attachments from '../components/Attachments';
import { InlineTextCell } from '../components/InlineTextCell';
import RmInvoicesModal, { RmActualCell, rmFlagsOf } from '../components/RmInvoicesModal';
import DataFreshness from '../components/DataFreshness';

const blank = () => ({
  account_owner_id: null,
  branch_id: null,
  quarter: '',
  date_planned: '',
  site: '',
  duration_days: '',
  equipment_type: '',
  estimated_value: '',
  win_pct: '', // sales sets it; never defaulted (server requires 0-100)
  actual_value: '',
  po_number: '',
  po_amount: '',
  po_date: '',
  lnl: false,
  msa: false,
  status: 'Bidding',
  notes: '',
  lost_reason: '',
  lost_reason_detail: ''
});

const LOST_REASONS = [
  'Price too high',
  'Lost to competitor',
  'No budget / project canceled',
  'Timing didn\'t match',
  'We declined to bid',
  'Other'
];

// Color tokens for the status pills (matches StatusPill in UI.jsx).
// In the Hunt + Closed added 2026-05 to mirror the Activity Log pipeline.
const STATUS_COLOR = {
  'In the Hunt': { bg: '#EDE9FE', fg: '#5B21B6' },  // indigo (prospecting)
  'Bidding':     { bg: '#FEF3C7', fg: '#92400E' },  // amber
  'Mobilization Standby': { bg: '#CFFAFE', fg: '#0E7490' },  // cyan (not won yet, be ready to mobilize)
  'Won':         { bg: '#D1FAE5', fg: '#065F46' },  // green
  'Active':      { bg: '#DBEAFE', fg: '#1E40AF' },  // blue (work in progress)
  'Complete':    { bg: '#F3F4F6', fg: '#4B5563' },  // gray
  'Lost':        { bg: '#FEE2E2', fg: '#991B1B' },  // red
  'Closed':      { bg: '#F3F4F6', fg: '#4B5563' }   // gray (canceled / postponed)
};

// Workflow order -- drives the pill toggle row and the dropdown options.
const ALL_PROJECT_STATUSES = ['In the Hunt', 'Bidding', 'Mobilization Standby', 'Won', 'Active', 'Complete', 'Lost', 'Closed'];

// Awarded work: the PO # cell is inline-editable on these rows (George
// 2026-09-14, "a place for the Salesman to add the PO to the project once
// awarded"), the same statuses the Operations board edits a PO on.
const PO_INLINE_STATUSES = new Set(['Won', 'Active', 'Complete']);
// Pre-award deals carry no Actual $: RentalMan fills it once Won, and the
// server refuses a typed one (server/utils/actualLock.js, keep in step).
const PRE_AWARD_STATUSES = new Set(['In the Hunt', 'Bidding', 'Mobilization Standby']);

// Stale-form revert fix (docs/SPEC-rm-invoiced-actuals.md section 7). save()
// used to re-send the WHOLE row loaded when the form opened, so a rep saving
// any field wrote back a status or PO that ops had changed in the meantime.
// Both the edited form and the snapshot go through the same coercions save()
// always applied, then only the keys that differ are sent.
const coerceForSave = (src) => {
  const p = { ...src };
  ['estimated_value','actual_value','po_amount','duration_days','win_pct'].forEach(k => {
    if (p[k] === '' || p[k] === null) p[k] = null;
    else p[k] = Number(p[k]);
  });
  if (p.po_date === '') p.po_date = null; // empty DATE must be null, not ''
  // A pre-award deal never sends a typed Actual (the server refuses one).
  if (PRE_AWARD_STATUSES.has(p.status)) p.actual_value = null;
  // If no exact date but the user typed a year, derive date_planned
  // from Quarter + Year (Q1->Jan 1, Q2->Apr 1, etc.). No quarter
  // provided defaults to Q1.
  if (!p.date_planned && /^20\d{2}$/.test(p.year_only || '')) {
    const q = ['Q1','Q2','Q3','Q4'].includes(p.quarter) ? p.quarter : 'Q1';
    const month = String((['Q1','Q2','Q3','Q4'].indexOf(q)) * 3 + 1).padStart(2, '0');
    p.date_planned = `${p.year_only}-${month}-01`;
  }
  delete p.year_only; // not a DB column
  if (!p.date_planned) p.date_planned = null;
  return p;
};
// '' from a touched-then-cleared input and NULL from the DB are the same
// answer, so neither counts as a change. Objects (rm_flags, labor_plan)
// compare by value.
const isBlank = (v) => v === null || v === undefined || v === '';
const sameValue = (a, b) => {
  if (isBlank(a) && isBlank(b)) return true;
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
};

// ---- Project Updates panel (weekly AM progress posts) ------------------
function ProjectUpdates({ projectId }) {
  const { user } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [body, setBody] = useState('');
  const [weekOf, setWeekOf] = useState(currentWeekOf());
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [editWeekOf, setEditWeekOf] = useState('');
  const [error, setError] = useState('');

  const reload = () =>
    api.listProjectUpdates(projectId).then(setUpdates).catch(e => setError(e.message));
  useEffect(() => { if (projectId) reload(); }, [projectId]);

  const submit = async () => {
    if (!body.trim()) return;
    setError('');
    try {
      await api.createProjectUpdate(projectId, { body: body.trim(), week_of: weekOf });
      setBody(''); setWeekOf(currentWeekOf()); reload();
    } catch (e) { setError(e.message); }
  };

  const saveEdit = async (id) => {
    if (!editBody.trim()) return;
    setError('');
    try {
      await api.updateProjectUpdate(id, { body: editBody.trim(), week_of: editWeekOf });
      setEditingId(null); reload();
    } catch (e) { setError(e.message); }
  };

  const startEdit = (u) => {
    setEditingId(u.id); setEditBody(u.body);
    setEditWeekOf(u.week_of ? u.week_of.slice(0, 10) : '');
  };

  const remove = async (id) => {
    if (!confirm('Delete this update?')) return;
    try { await api.deleteProjectUpdate(id); reload(); }
    catch (e) { setError(e.message); }
  };

  const canEdit = (u) => isAdminLike(user) || user?.id === u.author_id;

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
      <h2 style={styles.h2}>Weekly Updates</h2>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <textarea
            style={{ ...styles.input, resize: 'vertical', minHeight: 64, width: '100%', boxSizing: 'border-box' }}
            placeholder="This week's update…"
            value={body}
            onChange={e => setBody(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input
            type="date"
            style={{ ...styles.input, width: 160 }}
            value={weekOf}
            onChange={e => setWeekOf(e.target.value)}
            title="Week of"
          />
          <button style={styles.btn} onClick={submit} disabled={!body.trim()}>Post Update</button>
        </div>
      </div>
      {updates.length === 0 ? (
        <p style={{ color: '#6B7280', fontSize: 13, margin: 0 }}>No updates yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {updates.map(u => (
            <div key={u.id} style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, background: '#F9FAFB' }}>
              {editingId === u.id ? (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <textarea
                      style={{ ...styles.input, flex: '1 1 200px', minHeight: 60, resize: 'vertical', boxSizing: 'border-box' }}
                      value={editBody}
                      onChange={e => setEditBody(e.target.value)}
                    />
                    <input
                      type="date"
                      style={{ ...styles.input, width: 160 }}
                      value={editWeekOf}
                      onChange={e => setEditWeekOf(e.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={styles.btn} onClick={() => saveEdit(u.id)}>Save</button>
                    <button style={styles.btnSecondary} onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                      <strong style={{ color: '#1F2937' }}>{u.author_name}</strong>
                      {' · week of '}{u.week_of ? fmtDate(u.week_of) : '—'}
                      {' · '}{fmtDateTime(u.created_at)}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap', color: '#1F2937' }}>{u.body}</p>
                  {canEdit(u) && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button style={styles.btnSecondary} onClick={() => startEdit(u)}>Edit</button>
                      <button style={styles.btnDanger} onClick={() => remove(u.id)}>Delete</button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [reps, setReps] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  // The row as the edit form opened it; save() sends only what differs.
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState('');
  // Row whose RentalMan invoice list is open (RmInvoicesModal).
  const [rmFor, setRmFor] = useState(null);
  const [savingPoId, setSavingPoId] = useState(null);
  // status is an array of selected statuses (multi-select pills).
  // Empty array = no status filter (all). Default: all 7 enabled.
  // URL search params seed the initial filter so other pages can deep-link
  // (e.g. /projects?stuck=yes from the Dashboard greeting).
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState({
    status: searchParams.get('status')
      ? searchParams.get('status').split(',').filter(Boolean)
      : [...ALL_PROJECT_STATUSES],
    branch_id: searchParams.get('branch_id') || '',
    owner_id: searchParams.get('owner_id') || '',
    quarter: searchParams.get('quarter') || '',
    needs_review: searchParams.get('needs_review') || '',
    stuck: searchParams.get('stuck') || '',
    year: searchParams.get('year') ?? String(currentYear())
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [view, setView] = useState('table'); // table | board
  const isMobile = useIsMobile();

  // Debounce search input so we don't fire on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const reload = () => {
    const params = {};
    Object.entries(filter).forEach(([k, v]) => {
      if (k === 'needs_review') return;        // client-side filter
      if (k === 'status') {
        // Send as comma-list. Skip if all statuses selected (no filter
        // needed) or empty (would return zero rows -- treat as "all" to
        // be friendly).
        if (Array.isArray(v) && v.length > 0 && v.length < ALL_PROJECT_STATUSES.length) {
          params.status = v.join(',');
        }
        return;
      }
      if (v) params[k] = v;
    });
    if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
    api.listProjects(params).then(rs => {
      if (filter.needs_review === 'yes') setRows(rs.filter(r => r.needs_review));
      else if (filter.needs_review === 'no') setRows(rs.filter(r => !r.needs_review));
      else setRows(rs);
    }).catch(e => setError(e.message));
  };

  useEffect(reload, [filter.status.join(','), filter.branch_id, filter.owner_id, filter.quarter, filter.needs_review, filter.stuck, filter.year, debouncedSearch]);
  useEffect(() => {
    api.listBranches().then(setBranches).catch(() => {});
    if (isAdminLike(user) || user.role === 'supervisor') {
      api.listReps().then(setReps).catch(() => {});
    }
  }, [user.role]);

  // Scroll the edit form into view after it mounts. Without this, clicking
  // Edit on a row near the bottom of a long list silently re-renders the
  // form way up at the top and looks like nothing happened.
  const scrollToForm = () => setTimeout(() =>
    document.getElementById('project-edit-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  50);

  const startCreate = () => { setEditing(blank()); setSnapshot(null); scrollToForm(); };
  const startEdit = (p) => {
    const opened = {
      ...p,
      date_planned: p.date_planned?.slice(0,10) || '',
      estimated_value: p.estimated_value ?? '',
      actual_value: p.actual_value ?? '',
      po_amount: p.po_amount ?? '',
      po_date: p.po_date?.slice(0,10) || '',
      duration_days: p.duration_days ?? ''
    };
    setEditing(opened);
    setSnapshot(opened);
    scrollToForm();
  };
  const cancel = () => { setEditing(null); setSnapshot(null); };

  // Deep link /projects?edit=<id> (the Account page's Edit link) opens that
  // project's form directly, whatever the list filters would hide. The param
  // is dropped once read so a reload or Back does not reopen the form.
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam) return;
    api.getProject(editParam).then(startEdit).catch(e => setError(e.message));
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [editParam]);

  const save = async () => {
    setError('');
    // Win % is sales' number and is never defaulted; an awarded status is
    // forced to 100 by the server, so only the other statuses need one.
    if (!PO_INLINE_STATUSES.has(editing.status) && String(editing.win_pct ?? '').trim() === '') {
      setError('Win % is required -- enter the chance of winning, 0 to 100.');
      return;
    }
    // Revenue Type is required (server utils/revenueType.js): it picks the P&L
    // and whether a Won deal gets its TA Job # automatically.
    if (!editing.revenue_type) {
      setError('Choose the Revenue Type (Branch, TA or Rescue). TA deals get their TA Job # automatically when marked Won.');
      return;
    }
    // Same rule as the server (utils/poOnWon.js): entering an award needs a PO.
    if (PO_INLINE_STATUSES.has(editing.status) && !PO_INLINE_STATUSES.has(snapshot?.status)
        && !String(editing.po_number ?? '').trim()) {
      setError('Enter the customer PO # to mark this Won. If the PO has not arrived yet, enter TBD; you will get a reminder until the real PO is entered.');
      return;
    }
    try {
      const p = coerceForSave(editing);
      if (!p.account_owner_id) p.account_owner_id = user.id;
      if (editing.id) {
        // The owner default is applied to the edited side only: on an
        // unassigned row the dropdown already SHOWS the saving admin as the
        // owner, so saving has to claim it the way the whole-row save did.
        const before = coerceForSave(snapshot || {});
        const changes = {};
        for (const k of Object.keys(p)) {
          if (!sameValue(p[k], before[k])) changes[k] = p[k];
        }
        if (Object.keys(changes).length) await api.updateProject(editing.id, changes);
        else { setEditing(null); setSnapshot(null); return; } // nothing changed: no request
      } else {
        await api.createProject(p);
      }
      setEditing(null);
      setSnapshot(null);
      reload();
    } catch (err) { setError(err.message); }
  };

  // Inline PO # on an awarded row. Saves only po_number through the Tracker
  // PUT, which cleans it, logs it and recomputes the RentalMan Actual $; the
  // reload then shows the new figure. A failure reverts the cell.
  const onPoSave = async (id, po_number) => {
    setSavingPoId(id);
    setError('');
    try { await api.updateProject(id, { po_number }); reload(); }
    catch (err) { setError(err.message); throw err; }
    finally { setSavingPoId(null); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this project?')) return;
    await api.deleteProject(id);
    reload();
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ ...styles.h1, margin: 0 }}>Project Tracker</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <button onClick={() => setView('table')} style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: view === 'table' ? '#1F2937' : '#fff',
              color: view === 'table' ? '#fff' : '#1F2937'
            }}>Table</button>
            <button onClick={() => setView('board')} style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: view === 'board' ? '#1F2937' : '#fff',
              color: view === 'board' ? '#fff' : '#1F2937'
            }}>Board</button>
          </div>
          {isAdminLike(user) && (
            <button style={styles.btnSecondary} onClick={async () => {
              // Export the current filtered + searched view as xlsx (admin only)
              const params = {};
              Object.entries(filter).forEach(([k, v]) => { if (k !== 'needs_review' && v) params[k] = v; });
              if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
              try {
                const res = await api.exportProjects(params);
                if (!res.ok) throw new Error('Export failed');
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const stamp = new Date().toISOString().slice(0, 10);
                a.download = `sss-crm-projects-${stamp}.xlsx`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 100);
              } catch (err) {
                setError(err.message);
              }
            }}>↓ Export</button>
          )}
          <button style={styles.btn} onClick={startCreate}>+ New Project</button>
        </div>
      </div>

      {/* When the RentalMan report behind Actual $ was pulled (George 2026-09-14). */}
      <DataFreshness
        load={api.rmInvoiceFeed}
        rollupId="tracker-data-freshness"
        subtitle="the RentalMan report behind the Actual $ column"
        footer="Actual $ totals every RentalMan invoice carrying the opportunity's PO, as of that pull. sss-inspection and Field Services read the same report on their own schedules, so a total there can differ until both have loaded the same pull."
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
        <input
          style={{ ...styles.input, flex: 1, maxWidth: 480 }}
          placeholder="🔍  Search site, equipment, notes, PO #, account manager, branch..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button style={styles.btnSecondary} onClick={() => setSearch('')}>Clear</button>
        )}
        <span style={{ fontSize: 12, color: '#6B7280' }}>{rows.length} result{rows.length === 1 ? '' : 's'}</span>
      </div>

      {/* Primary filters: branch + rep first, then year, then refinements */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...styles.input, width: 200 }} value={filter.branch_id} onChange={e => {
          const newBranch = e.target.value;
          // If the currently-selected rep isn't in the new branch, clear it
          const repIsInNewBranch = !filter.owner_id ||
            (newBranch === '' || reps.find(r => String(r.id) === filter.owner_id && String(r.branch_id) === newBranch));
          setFilter({ ...filter, branch_id: newBranch, owner_id: repIsInNewBranch ? filter.owner_id : '' });
        }}>
          <option value="">All branches</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
        </select>
        {(isAdminLike(user) || user.role === 'supervisor') && (
          <select style={{ ...styles.input, width: 220 }} value={filter.owner_id} onChange={e => setFilter({ ...filter, owner_id: e.target.value })}>
            <option value="">All account managers</option>
            {(filter.branch_id
              ? reps.filter(r => String(r.branch_id) === String(filter.branch_id))
              : reps
            ).map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
          </select>
        )}
        <select style={{ ...styles.input, width: 130 }} value={filter.year} onChange={e => setFilter({ ...filter, year: e.target.value })}>
          <option value="">All years</option>
          {yearOptions().map(y => <option key={y} value={String(y)}>{y}</option>)}
          <option value="undated">No date</option>
        </select>
        <select style={{ ...styles.input, width: 160 }} value={filter.quarter} onChange={e => setFilter({ ...filter, quarter: e.target.value })}>
          <option value="">All quarters</option>
          <option>Q1</option><option>Q2</option><option>Q3</option><option>Q4</option>
        </select>
        <select style={{ ...styles.input, width: 180 }} value={filter.needs_review} onChange={e => setFilter({ ...filter, needs_review: e.target.value })}>
          <option value="">All projects</option>
          <option value="yes">⚑ Needs review only</option>
          <option value="no">Reviewed / complete</option>
        </select>
        <select style={{ ...styles.input, width: 180 }} value={filter.stuck} onChange={e => setFilter({ ...filter, stuck: e.target.value })}>
          <option value="">Any age</option>
          <option value="yes">🕓 Stuck (30+ days)</option>
        </select>
      </div>

      {/* Status pills -- exclusive select. All on by default; clicking a
          pill shows only that status; clicking the lone-active pill again
          (or "reset") brings them all back. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginRight: 4 }}>STATUS:</span>
        {ALL_PROJECT_STATUSES.map(s => {
          const on = filter.status.includes(s);
          const isLoneActive = filter.status.length === 1 && filter.status[0] === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter({
                ...filter,
                status: isLoneActive ? [...ALL_PROJECT_STATUSES] : [s]
              })}
              style={{
                padding: '4px 12px', borderRadius: 14, fontSize: 12, fontWeight: 700,
                cursor: 'pointer', border: '1px solid',
                background: on ? STATUS_COLOR[s].bg : '#fff',
                color: on ? STATUS_COLOR[s].fg : '#9CA3AF',
                borderColor: on ? STATUS_COLOR[s].fg : '#E5E7EB',
                opacity: on ? 1 : 0.6
              }}
              title={isLoneActive ? 'Click to show all statuses' : `Show only ${s}`}
            >
              {on ? '✓ ' : ''}{s}
            </button>
          );
        })}
        {filter.status.length < ALL_PROJECT_STATUSES.length && (
          <button type="button" onClick={() => setFilter({ ...filter, status: [...ALL_PROJECT_STATUSES] })}
            style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', textDecoration: 'underline', marginLeft: 4 }}>
            reset
          </button>
        )}
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {editing && (
        <div id="project-edit-form" style={styles.card}>
          <h2 style={styles.h2}>{editing.id ? 'Edit Project' : 'New Project'}</h2>
          <div style={styles.formGrid}>
            <Field label="Quarter">
              <select style={styles.input} value={editing.quarter || ''} onChange={e => setEditing({ ...editing, quarter: e.target.value })}>
                <option value="">—</option><option>Q1</option><option>Q2</option><option>Q3</option><option>Q4</option>
              </select>
            </Field>
            <Field label="Year (if no exact date)">
              <input type="text" style={styles.input} placeholder="e.g. 2028" maxLength={4} pattern="20\d{2}"
                value={editing.year_only || ''}
                onChange={e => setEditing({ ...editing, year_only: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
            </Field>
            <Field label="Date Planned (exact)">
              <input type="date" style={styles.input} value={editing.date_planned || ''} onChange={e => setEditing({ ...editing, date_planned: e.target.value })} />
            </Field>
            <Field label="Site *">
              <input style={styles.input} value={editing.site} onChange={e => setEditing({ ...editing, site: e.target.value })} />
            </Field>
            <Field label="Duration (days)">
              <input type="number" style={styles.input} value={editing.duration_days} onChange={e => setEditing({ ...editing, duration_days: e.target.value })} />
            </Field>
            <Field label="Branch">
              <select style={styles.input} value={editing.branch_id || ''} onChange={e => setEditing({ ...editing, branch_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Revenue Type *">
              <select style={styles.input} value={editing.revenue_type || ''} required
                onChange={e => setEditing({ ...editing, revenue_type: e.target.value })}
                title="Required. Which P&L this opportunity's revenue lands in -- a bid written out of a branch is often booked as TA or Rescue revenue. TA deals get their TA Job # automatically when marked Won.">
                <option value="" disabled>Choose...</option>
                <option value="Branch">Branch Revenue</option>
                <option value="TA">TA Revenue</option>
                <option value="Rescue">Rescue Revenue</option>
              </select>
            </Field>
            <Field label={isAdminLike(user) ? "Account Manager (Leadership can reassign)" : "Account Manager"}>
              {isAdminLike(user) ? (
                <select style={styles.input} value={editing.account_owner_id || user.id} onChange={e => setEditing({ ...editing, account_owner_id: Number(e.target.value) })}>
                  {reps.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                </select>
              ) : (
                <input style={styles.input} value={editing.account_owner_name || user.full_name} disabled />
              )}
            </Field>
            <Field label="Estimated Value ($)">
              <input type="number" style={styles.input} value={editing.estimated_value} onChange={e => setEditing({ ...editing, estimated_value: e.target.value })} />
            </Field>
            <Field label="% Win *">
              <input type="number" min="0" max="100" step="1" required style={styles.input} value={editing.win_pct ?? ''} onChange={e => setEditing({ ...editing, win_pct: e.target.value })} />
            </Field>
            <Field label="Actual Value ($)">
              {/* RentalMan owns an rm-sourced Actual (the server ignores a typed
                  one on those rows), so the field is read-only rather than
                  letting an edit silently vanish on save. */}
              {(() => {
                const preAward = PRE_AWARD_STATUSES.has(editing.status);
                const locked = preAward || editing.actual_source === 'rm';
                return (
                  <input type="number"
                    style={locked ? { ...styles.input, background: '#F3F4F6', color: '#374151' } : styles.input}
                    value={preAward ? '' : editing.actual_value}
                    disabled={locked}
                    placeholder={preAward ? 'Fills from RentalMan once Won' : undefined}
                    onChange={e => setEditing({ ...editing, actual_value: e.target.value })} />
                );
              })()}
              {editing.actual_source === 'rm' && !PRE_AWARD_STATUSES.has(editing.status) && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>
                  From RentalMan invoices ({editing.rm_invoice_count ?? 0})
                </div>
              )}
            </Field>
            <Field label="PO #">
              <input style={styles.input} value={editing.po_number || ''}
                placeholder={PO_INLINE_STATUSES.has(editing.status) ? 'Required once Won (TBD if not received)' : undefined}
                onChange={e => setEditing({ ...editing, po_number: e.target.value })} />
            </Field>
            <Field label="PO Amount ($)">
              <input type="number" style={styles.input} value={editing.po_amount} onChange={e => setEditing({ ...editing, po_amount: e.target.value })} />
            </Field>
            <Field label="PO Date">
              <input type="date" style={styles.input} value={editing.po_date || ''} onChange={e => setEditing({ ...editing, po_date: e.target.value })} />
            </Field>
            <Field label="Status">
              <select style={styles.input} value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })}>
                {ALL_PROJECT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="L&L">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                <input type="checkbox" checked={!!editing.lnl} onChange={e => setEditing({ ...editing, lnl: e.target.checked })} />
                Lunch &amp; Learn done
              </label>
            </Field>
            <Field label="MSA">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                <input type="checkbox" checked={!!editing.msa} onChange={e => setEditing({ ...editing, msa: e.target.checked })} />
                MSA in place
              </label>
            </Field>
          </div>
          <Field label="Equipment Type">
            <textarea style={styles.textarea} value={editing.equipment_type || ''} onChange={e => setEditing({ ...editing, equipment_type: e.target.value })} />
          </Field>
          <Field label="Notes">
            <textarea style={styles.textarea} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} />
          </Field>

          {/* Lost-reason capture: only render when status = Lost */}
          {editing.status === 'Lost' && (
            <div style={{ marginTop: 12, padding: 12, background: '#FEF2F2', borderRadius: 6, border: '1px solid #FCA5A5' }}>
              <h2 style={{ ...styles.h2, color: '#991B1B', margin: 0, marginBottom: 8 }}>Why did we lose this?</h2>
              <div style={styles.formGrid}>
                <Field label="Reason">
                  <select style={styles.input} value={editing.lost_reason || ''} onChange={e => setEditing({ ...editing, lost_reason: e.target.value })}>
                    <option value="">— pick one —</option>
                    {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
                <Field label="Detail (optional)">
                  <input style={styles.input} placeholder={editing.lost_reason === 'Lost to competitor' ? 'Which competitor?' : 'Free text'} value={editing.lost_reason_detail || ''} onChange={e => setEditing({ ...editing, lost_reason_detail: e.target.value })} />
                </Field>
              </div>
            </div>
          )}

          {editing.id && editing.needs_review && (
            <div style={{ marginTop: 12, padding: 10, background: '#FEF3C7', borderRadius: 6 }}>
              <div style={{ fontSize: 13, color: '#92400E' }}>
                ⚑ <strong>Needs review</strong> — {editing.review_notes || 'missing key fields'}
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0 18px' }}>
                Fill in the missing fields above and Save — the flag clears automatically.
              </p>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={styles.btn} onClick={save}>Save</button>
            <button style={styles.btnSecondary} onClick={cancel}>Cancel</button>
          </div>
          {editing.id && editing.last_contact_at && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#6B7280' }}>
              📇 Last contact logged: <strong style={{ color: '#374151' }}>{fmtDateTime(editing.last_contact_at)}</strong>
              <span style={{ marginLeft: 4 }}>(from a Sales Activity)</span>
            </div>
          )}
          {editing.id && <Attachments projectId={editing.id} />}
          {editing.id && <ProjectUpdates projectId={editing.id} />}
        </div>
      )}

      {view === 'board' ? (
        <KanbanBoard rows={rows} onCardClick={startEdit} isMobile={isMobile} canOpenEstimate={isAdminLike(user)} />
      ) : isMobile ? (
        <div>
          {rows.length === 0 && <div style={mobile.emptyHint}>No projects.</div>}
          {rows.map(p => {
            const cardStyle = p.needs_review
              ? { ...mobile.listItem, background: '#FEF3C7', borderColor: '#F59E0B' }
              : mobile.listItem;
            const rmFlags = rmFlagsOf(p.rm_flags);
            const showActual = p.actual_value != null || rmFlags.no_invoices || Number(rmFlags.held_out_amount) > 0;
            return (
              <div key={p.id} style={cardStyle}>
                <div style={mobile.listItemHeader}>
                  <Link to={`/accounts/${encodeURIComponent(p.site)}`} style={{ ...mobile.listItemTitle, color: '#E36F2C', textDecoration: 'none' }}>
                    {p.site}
                  </Link>
                  <StatusPill status={p.status} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                  {p.needs_review && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#FEF3C7', color: '#92400E' }}
                      title={p.review_notes || 'Needs review'}>⚑ Needs review</span>
                  )}
                  {p.stuck && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: '#FEF3C7', color: '#92400E' }}
                      title={`No update in ${p.days_since_update} days`}>🕓 Stuck</span>
                  )}
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Date</span>
                  <span style={mobile.listValue}>{fmtDate(p.date_planned) || '—'}{p.quarter ? ` · ${p.quarter}` : ''}{p.duration_days ? ` · ${p.duration_days}d` : ''}</span>
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Branch</span>
                  <span style={mobile.listValue}>{p.branch_name || '—'}</span>
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Acct Mgr</span>
                  <span style={mobile.listValue}>{p.account_owner_name || '—'}</span>
                </div>
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Est. $ · % Win</span>
                  <span style={mobile.listValue}>{fmtMoney(p.estimated_value)} · {p.win_pct}%</span>
                </div>
                {showActual && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>Actual $</span>
                    {/* Column flex so the RM link and notes stack under the
                        figure, right-aligned like every other card value. */}
                    <span style={{ ...mobile.listValue, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <RmActualCell
                        project={p}
                        onOpen={setRmFor}
                        suffix={p.variance != null ? ` (var ${fmtMoney(p.variance)})` : null}
                      />
                    </span>
                  </div>
                )}
                {p.po_number && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>PO #</span>
                    <span style={mobile.listValue}>{p.po_number}</span>
                  </div>
                )}
                {p.equipment_type && (
                  <div style={{ ...mobile.listRow, flexDirection: 'column', alignItems: 'stretch' }}>
                    <span style={mobile.listLabel}>Equipment</span>
                    <span style={{ ...mobile.listValue, textAlign: 'left', whiteSpace: 'pre-wrap', marginTop: 4, fontSize: 13 }}>{p.equipment_type}</span>
                  </div>
                )}
                <div style={mobile.listActions}>
                  <button style={{ ...styles.btnSecondary, ...mobile.btnTap, flex: 1 }} onClick={() => startEdit(p)}>Edit</button>
                  <button style={{ ...styles.btnDanger, ...mobile.btnTap }} onClick={() => remove(p.id)}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Q</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Dur.</th>
              <th style={styles.th}>L&amp;L</th>
              <th style={styles.th}>MSA</th>
              <th style={styles.th}>Equipment</th>
              <th style={styles.th}>Est. $</th>
              <th style={styles.th}>%W</th>
              <th style={styles.th}>Rev. Prob.</th>
              <th style={styles.th}>Actual $</th>
              <th style={styles.th}>Var.</th>
              <th style={styles.th}>PO #</th>
              <th style={styles.th}>Branch</th>
              <th style={styles.th}>Acct Mgr</th>
              <th style={styles.th}>Last Update</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Flag</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={19} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>No projects.</td></tr>
            )}
            {rows.map(p => (
              <tr key={p.id} style={p.needs_review ? { background: '#FEF3C7' } : {}}>
                <td style={styles.td}>{p.quarter}</td>
                <td style={styles.td}>{fmtDate(p.date_planned)}</td>
                <td style={styles.td}>
                  <Link to={`/accounts/${encodeURIComponent(p.site)}`} style={{ color: '#E36F2C', fontWeight: 600, textDecoration: 'none' }}>
                    {p.site}
                  </Link>
                  {p.stuck && (
                    <span title={`No update in ${p.days_since_update} days`} style={{
                      marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#92400E',
                      background: '#FEF3C7', padding: '1px 6px', borderRadius: 8
                    }}>
                      🕓 STUCK
                    </span>
                  )}
                </td>
                <td style={styles.td}>{p.duration_days ? p.duration_days + 'd' : ''}</td>
                <td style={styles.td}>{p.lnl ? 'Y' : ''}</td>
                <td style={styles.td}>{p.msa ? 'Y' : ''}</td>
                <td style={{ ...styles.td, maxWidth: 240, whiteSpace: 'pre-wrap', fontSize: 12 }}>{p.equipment_type}</td>
                <td style={styles.td}>{fmtMoney(p.estimated_value)}</td>
                <td style={styles.td}>{p.win_pct}%</td>
                <td style={styles.td}>{fmtMoney(p.revenue_probability)}</td>
                <td style={styles.td}><RmActualCell project={p} onOpen={setRmFor} /></td>
                <td style={styles.td}>{p.variance != null ? fmtMoney(p.variance) : ''}</td>
                <td style={styles.td}>
                  {PO_INLINE_STATUSES.has(p.status) ? (
                    <InlineTextCell
                      value={p.po_number}
                      disabled={savingPoId === p.id}
                      onSave={(v) => onPoSave(p.id, v)}
                      maxLength={100}
                      width={100}
                      subtle
                    />
                  ) : p.po_number}
                </td>
                <td style={styles.td}>{p.branch_name || ''}</td>
                <td style={styles.td}>{p.account_owner_name || ''}</td>
                <td style={styles.td}>
                  {p.last_update_at ? (
                    <span style={{ fontSize: 11, color: '#6B7280' }} title={fmtDateTime(p.last_update_at)}>
                      {fmtDate(p.last_update_at)}
                    </span>
                  ) : ''}
                </td>
                <td style={styles.td}><StatusPill status={p.status} /></td>
                <td style={styles.td}>
                  {p.needs_review && (
                    <span title={p.review_notes || 'Needs review'} style={{ color: '#92400E', fontSize: 16 }}>⚑</span>
                  )}
                </td>
                <td style={styles.td}>
                  <button style={styles.btnSecondary} onClick={() => startEdit(p)}>Edit</button>{' '}
                  <button style={styles.btnDanger} onClick={() => remove(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {rmFor && (
        <RmInvoicesModal
          projectId={rmFor.id}
          source="sales"
          title={`RentalMan invoices · ${rmFor.site}${rmFor.job_number ? ` · ${rmFor.job_number}` : ''}`}
          onClose={() => setRmFor(null)}
        />
      )}
    </>
  );
}

// ---- Kanban Board view ------------------------------------------------

const COLUMNS = [
  { key: 'In the Hunt', label: 'In the Hunt', color: '#7C3AED' },
  { key: 'Bidding',     label: 'Bidding',     color: '#F59E0B' },
  { key: 'Mobilization Standby', label: 'Mobilization Standby', color: '#0E7490' },
  { key: 'Won',         label: 'Won',         color: '#10B981' },
  { key: 'Active',      label: 'Active',      color: '#2563EB' },
  { key: 'Complete',    label: 'Complete',    color: '#9CA3AF' },
  { key: 'Lost',        label: 'Lost',        color: '#EF4444' },
  { key: 'Closed',      label: 'Closed',      color: '#6B7280' }
];

function KanbanBoard({ rows, onCardClick, isMobile, canOpenEstimate }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)',
      gap: 12, alignItems: 'start'
    }}>
      {COLUMNS.map(col => {
        const cards = rows.filter(r => r.status === col.key);
        // Both totals matter -- Forecast for pipeline math, Actual for
        // billed-to-date tracking (column shows whichever are non-zero).
        const totalForecast = cards.reduce((s, r) => s + Number(r.estimated_value || 0), 0);
        const totalActual   = cards.reduce((s, r) => s + Number(r.actual_value || 0), 0);
        return (
          <div key={col.key} style={{
            background: '#F8FAFC', borderRadius: 8, border: '1px solid #E5E7EB',
            padding: 8, minHeight: 200
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.5, color: col.color, marginBottom: 8, padding: '4px 6px',
              borderBottom: `2px solid ${col.color}`
            }}>
              {col.label} <span style={{ color: '#6B7280', fontWeight: 400 }}>· {cards.length}</span>
              {(totalForecast > 0 || totalActual > 0) && (
                <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, marginTop: 2, lineHeight: 1.4 }}>
                  {totalForecast > 0 && <div>Fcst: {fmtMoney(totalForecast)}</div>}
                  {totalActual > 0   && <div>Actual: {fmtMoney(totalActual)}</div>}
                </div>
              )}
            </div>
            {cards.length === 0 && (
              <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', padding: 8 }}>—</div>
            )}
            {cards.map(p => (
              <div
                key={p.id}
                onClick={() => onCardClick(p)}
                style={{
                  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6,
                  padding: 8, marginBottom: 6, cursor: 'pointer', fontSize: 12,
                  boxShadow: p.stuck ? '0 0 0 2px #FEF3C7' : 'none'
                }}
                title="Click to edit"
              >
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#1F2937' }}>
                  {p.site}
                </div>
                <div style={{ color: '#6B7280', fontSize: 11, marginBottom: 4 }}>
                  {p.account_owner_name || '—'} · {p.branch_name || ''}
                </div>
                {/* Forecast (always) + Win% in one row; Actual on its own
                    row only when it's been entered. Matches the list view
                    columns (Forecast / Actual / Win %) but stacked. */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 11 }}>
                    <span style={{ color: '#6B7280' }}>Fcst: </span>
                    <strong style={{ color: '#1F2937' }}>{fmtMoney(p.estimated_value)}</strong>
                  </span>
                  <span style={{ fontSize: 10, color: '#6B7280' }}>{p.win_pct}%</span>
                </div>
                {p.actual_value != null && (
                  <div style={{ fontSize: 11, marginTop: 2 }}>
                    <span style={{ color: '#6B7280' }}>Actual: </span>
                    <strong style={{ color: '#065F46' }}>{fmtMoney(p.actual_value)}</strong>
                  </div>
                )}
                {/* Quarter · Date -- same shape as the list view's
                    Quarter + Planned Date columns. */}
                {(p.quarter || p.date_planned) && (
                  <div style={{ fontSize: 10, color: '#6B7280', marginTop: 4 }}>
                    {p.quarter || ''}{p.quarter && p.date_planned ? ' · ' : ''}{p.date_planned ? fmtDate(p.date_planned) : ''}
                  </div>
                )}
                {/* Linked estimate(s), current revision per chain (George
                    2026-10-20). Click-through for the roles that can open
                    Estimating; everyone else sees the label only. */}
                {(p.linked_estimates || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {p.linked_estimates.map(e => {
                      const label = `${e.is_co ? 'CO ' : ''}EST-${e.root_id}-${e.revision} · ${e.status}`;
                      const chip = {
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10,
                        background: e.status === 'Approved' ? '#D1FAE5' : '#F3F4F6',
                        color: e.status === 'Approved' ? '#065F46' : '#374151',
                        textDecoration: 'none', whiteSpace: 'nowrap'
                      };
                      return canOpenEstimate ? (
                        <Link key={e.id} to={`/estimating?open=${e.id}`} style={chip}
                          onClick={ev => ev.stopPropagation()} title="Open this estimate">
                          {label}
                        </Link>
                      ) : (
                        <span key={e.id} style={chip}>{label}</span>
                      );
                    })}
                  </div>
                )}
                {p.stuck && (
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#92400E', marginTop: 4 }}>🕓 STUCK</div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
);
